import React, { useState } from 'react';
import DataTableFilter from 'Components/shared/data-table-filter/data-table-filter';
import ConfirmModal from 'Components/shared/confirm-modal/confirm-modal';
import StatusSelector from 'Components/shared/status-selector/status-selector';
import { DataTable } from '@life-line-lab/components-react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActionMenu, SkeletonLoader } from '@life-line-lab/components-react';
import Icon, { MailOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { map, isEmpty } from 'lodash';

interface ModalState {
    isOpen: boolean;
    header: React.ReactNode;
    content: React.ReactNode;
    btnClassName?: string;
    callback?: (ids: string[]) => void;
}

interface AccountTableProps {
    list: any[];
    isShowFilter?: boolean;
    orgID: string | boolean;
    // permissionRights: string[];
    onSearch?: (text: string) => void;
    onFilter?: (filters: { [key: string]: string | boolean }) => void;
    onRoleStatusChange?: (profileID: string, role: any, active: boolean) => void;
    isLoading?: boolean;
}

const AccountTableNew: React.FC<AccountTableProps> = ({ list, orgID, isShowFilter = true, onSearch, isLoading, onFilter, onRoleStatusChange }) => {

    const { t } = useTranslation();
    const history = useHistory();
    const initialModel = { isOpen: false, header: null, content: null, btnClassName: '', callback: () => { } };

    const [modalState, setModalState] = useState<ModalState>(initialModel);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [isBatchActionActive, setIsBatchActionActive] = useState<boolean>(false);

    const handleModalClose = () => {
        setModalState(initialModel);
    }

    const handleModalCancel = () => {
        setModalState(initialModel);
    }

    const handleFilter = (extraFilters: { [key: string]: string | boolean }) => {
        if (onFilter) {
            setSelectedRows([]);
            setIsBatchActionActive(false);
            onFilter(extraFilters)
        }
    }

    const rowSelection = {
        onSelect: (record: any, selected: boolean, selectedRows: any[]) => {
            const isActiveBatchAction = selectedRows.length > 0;
            setSelectedRows([...selectedRows]);
            setIsBatchActionActive(isActiveBatchAction);
        },
        onSelectAll: (selected: boolean, selectedRows: any[]) => {
            const isActiveBatchAction = selectedRows.length > 0;
            setSelectedRows([...selectedRows]);
            setIsBatchActionActive(isActiveBatchAction);
        }
    }

    const columns = [
        {
            title: t('label.name'),
            dataIndex: ['fullName'],
            width: '200px',
            render: (_: any, record: any) => {
                return (
                    <React.Fragment>
                        <div>
                            <a onClick={
                                (e: React.MouseEvent<HTMLElement>) =>
                                    handleActionClick('UPDATE', record)
                            }>
                                {record.fullName}
                            </a>
                        </div>
                    </React.Fragment>
                )
            },
        },
        {
            title: t('label.gender'),
            dataIndex: ['gender'],
            render: (text: string) => (
                <span>
                    {text ? t(`option.${text.toLowerCase().replace(/ /g, "")}`) : ''}
                </span>
            ),
        },
        {
            title: t('label.contact'),
            dataIndex: ['contactNumber', 'fullNumber'],
            render: (_: any, record: any) => {
                return (
                    <React.Fragment>
                        <div className="meta-data">
                            {record.contactNumber ?
                                <span>
                                    <Icon component={() => (
                                        <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" width="15px" height="15px" p-id="20109">
                                            <path d="M878.52 454.35c-9.48-195.74-175.85-346.73-371.59-337.25-182.47 8.84-328.4 154.78-337.25 337.25a159.918 159.918 0 0 0-50.49 116.43c0 60.05 35.64 131.36 91.65 131.36 50.49 0 50.49-56.29 50.49-131.36 0-69.43 0-125.72-33.67-131.38 17.06-163.32 163.3-281.89 326.62-264.83 138.36 14.45 248.2 122.91 264.41 261.07-33.67 11.26-33.67 61.92-33.67 131.36a348.65 348.65 0 0 0 9.36 110.73A316.037 316.037 0 0 1 601.74 820.3a99.248 99.248 0 0 0-80.43-35.64c-50.49 0-91.65 26.28-91.65 56.29s41.16 56.29 91.65 56.29 91.65-26.28 91.65-56.29a342.73 342.73 0 0 0 196.39-146.37c5.61 5.63 14.97 5.63 26.14 5.63 56.1 0 91.65-71.29 91.65-131.36a150.407 150.407 0 0 0-48.62-114.5z" p-id="20110"></path>
                                            <path d="M486.17 547.1c18.2 0.01 32.97-14.73 32.98-32.94v-81.62c0.01-18.2-14.73-32.97-32.94-32.98h-0.04c-17.96 0.6-32.38 15.02-32.98 32.98v81.58c-0.01 18.2 14.73 32.97 32.94 32.98h0.04z m187.46-34.72V430.8c0.01-18.2-14.73-32.97-32.94-32.98h-0.04c-17.96 0.61-32.37 15.02-32.98 32.98v81.69c-0.05 18.21 14.68 33.02 32.89 33.07 18.21 0.05 33.02-14.68 33.07-32.89v-0.29z" p-id="20111"></path>
                                        </svg>
                                    )} /> &nbsp;
                                    <span onClick={() => handleCopyToClipboard(record.contactNumber?.fullNumber)}>
                                        {`+${record.contactNumber?.fullNumber}`}
                                    </span>
                                </span> : ''
                            }
                        </div>
                        <div className="meta-data">
                            {record.email ?
                                <span>
                                    <MailOutlined /> &nbsp;
                                    <span onClick={() => handleCopyToClipboard(record.email)}>
                                        {record.email}
                                    </span>
                                </span> : ''
                            }
                        </div>
                    </React.Fragment>
                )
            },
        },
        {
            title: t('label.status'),
            dataIndex: ['status'],
            render: (_: any, record: any) => {
                return (
                    <React.Fragment>
                        {!isEmpty(record.roles) && map(record.roles, (role, index) => {
                            return (
                                <div className="role-status" key={index}>
                                    <div className="role-status--label">
                                        {t(`label.${role.subType}`)}
                                    </div>

                                    <div className="role-status--status">
                                        {role.status !== 'PENDING_ACCOUNT' &&
                                            <StatusSelector
                                                status={role.status}
                                                options={[
                                                    {
                                                        label: t('option.ACTIVE'),
                                                        value: 'ACTIVE',
                                                        onClick: () => handleRoleStatusChange({ profileID: record.profileID, role, status: 'ACTIVE' }),
                                                        isDisable: role.status === 'ACTIVE',
                                                    },
                                                    {
                                                        label: t('option.DEACTIVATED'),
                                                        value: 'DEACTIVATED',
                                                        onClick: () => handleRoleStatusChange({ profileID: record.profileID, role, status: 'DEACTIVATED' }),
                                                        isDisable: record.status === 'DEACTIVATED',
                                                    }
                                                ]}
                                            />
                                        }

                                        {role.status === 'PENDING_ACCOUNT' &&
                                            <span className={`status ${role.status}`}>
                                                {t(`option.${role.status}`)}
                                            </span>
                                        }
                                    </div>
                                </div>
                            )
                        })}
                    </React.Fragment>
                    // <React.Fragment>

                    // </React.Fragment>
                )
            },
        },
        {
            title: '',
            dataIndex: 'actions',
            width: '100px',
            render: (_: any, record: any, rowIndex: string) => (
                <ActionMenu
                    label={
                        <Icon component={() => (
                            <svg viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                <path d="M938.666667 512c0-234.666667-192-426.666667-426.666667-426.666667S85.333333 277.333333 85.333333 512s192 426.666667 426.666667 426.666667 426.666667-192 426.666667-426.666667zM170.666667 512c0-187.733333 153.6-341.333333 341.333333-341.333333s341.333333 153.6 341.333333 341.333333-153.6 341.333333-341.333333 341.333333-341.333333-153.6-341.333333-341.333333z m149.333333-85.333333l192 256 192-256h-384z" p-id="2219"></path>
                            </svg>
                        )} />
                    }
                    actions={[
                        {
                            label: t('action.edit'),
                            icon: <EditOutlined />,
                            onClick: () => handleActionClick('UPDATE', record),
                            isDisable: false,
                        }
                    ]}
                />
            )
        },
    ]

    return (
        <div className="account-table">
            {isShowFilter &&
                <DataTableFilter
                    searchLabel={t('label.search')}
                    filterLabel={t('label.filters')}
                    searchPlaceholder={t('txt.searchPlaceholder')}
                    onFilter={handleFilter}
                    onSearch={onSearch}
                    renderFilterSelection={{
                        statusFilters: {
                            label: t('label.status'),
                            options: [
                                { label: t('option.all'), value: '' },
                                { label: t('option.active'), value: 'ACTIVE' },
                                { label: t('option.inactive'), value: 'INACTIVE' },
                            ]
                        }
                    }}
                />
            }

            {isLoading &&
                <SkeletonLoader type="table" />
            }

            {!isLoading &&
                <DataTable
                    rowKey={(record: any) => { return record._id }}
                    columns={columns}
                    dataSource={list || []}
                    rowSelection={isShowFilter ? rowSelection : undefined}
                />
            }

            <ConfirmModal
                isOpen={modalState.isOpen}
                onConfirm={modalState.callback}
                onCancel={handleModalCancel}
                onClose={handleModalClose}
                renderHeader={modalState.header}
                cancelButtonProps={{
                    label: t('action.cancel'),
                }}
                confirmButtonProps={{
                    className: modalState.btnClassName,
                    label: t('action.confirm'),
                }}
            >
                {modalState.content}
            </ConfirmModal>
        </div>
    )
}

export default AccountTableNew
